/**
 * Data access for the unified inbox.
 *
 * The active brand rides on the session, so no brand id is sent for the normal
 * view. The one exception is the admin master view, which asks for `scope=all`
 * explicitly and may then narrow to a single brand. The server enforces the admin
 * check; the client only offers the control.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { UseQueryResult } from '@tanstack/react-query'
import { COMPLETE_LIST_LIMIT } from '../../components/pagination-model'
import { api } from '../../lib/api'
import { qk } from '../../lib/queryClient'
import type {
  CampaignSummary,
  Channel,
  ChannelAccount,
  ClassificationLabel,
  Conversation,
  ConversationSummary,
  LogReplyInput,
  Paginated,
  ProspectSummary,
  ReplySendResult,
} from '../../lib/types'
import type { AutoLabelOpenThreadsResult, AutoLabelState } from './autolabel'
import { replyRequestBody, type ReplyPayload } from './components/composer'
import { listParam, type InboxStateFilter, type InboxView, type ReminderFilter } from './filters'
import type { InboxCounts } from './rail'
import { withReadState } from './row'

export interface InboxFilters {
  /** Every brand at once. Admins only, enforced server-side. */
  master: boolean
  brandId: string | null
  /*
    THE FILTERS PANEL'S LISTS. The route takes any number of each, and a thread
    passes when it matches one value in every included category and none in any
    excluded one. A sequence matches when the thread's person is enrolled in it.
  */
  campaignIds: string[]
  excludeCampaignIds: string[]
  /** The domain word is channel. The word on screen is platform. */
  channels: Channel[]
  excludeChannels: Channel[]
  senderAccountIds: string[]
  excludeSenderAccountIds: string[]
  /** Only threads on an account the signed-in person owns, in this brand or, with `master`, in every brand. */
  mine: boolean
  /** Only threads on the accounts one person owns, picked from the person menu. Null or absent is everyone. */
  owner?: string | null
  /** Due, scheduled, or both, judged at the server's clock. Include only, as the reference has it. */
  reminder: ReminderFilter[]
  classification: ClassificationLabel | null
  /** Only threads the classifier could not call with confidence. */
  needsReview: boolean
  q: string
  /**
   * Which side of the archive to read. Null is the open inbox, and the
   * parameter is then left off the request entirely rather than sent as `open`.
   */
  state: InboxStateFilter | null
  /** The list's tab. Null is All. Composes with everything else here. */
  view: InboxView | null
  /** One of the workspace's own labels. Never a classifier label. */
  labelId: string | null
  /** The rail's No label row. Never sent alongside `labelId`: the server refuses the pair. */
  unlabelled: boolean
  /** The rail's AI conversations row: threads holding a model-written draft that is still pending. */
  aiDraft: boolean
}

/** One of a brand's labels, as every label route hands it out. */
export interface InboxLabel {
  id: string
  brandId: string
  name: string
  colour: string
  /**
   * One of the labels every workspace starts with (Closed, Meeting booked,
   * Meeting completed), drawn in the rail's Labels section beside the
   * classifier's rather than under Custom labels. A person applies them exactly
   * like a custom label. They cannot be renamed or deleted, because the rail's
   * layout is built around them.
   */
  system: boolean
  /**
   * When the label fits, in the workspace's own words, or null (0153).
   * Auto-label reads it beside the name. Absent from a server that predates
   * Auto-label, which reads the same as none.
   */
  applyWhen?: string | null
  createdAt: string
  updatedAt: string
}

/** Bare roots, so invalidation catches every filtered variant of a list. */
const CONVERSATIONS_ROOT = ['conversations'] as const
const APPROVALS_ROOT = ['approvals'] as const
const CARDS_ROOT = ['cards'] as const
const INBOX_COUNTS_ROOT = ['inbox', 'counts'] as const
const LABELS_ROOT = ['labels'] as const
const AUTOLABEL_ROOT = ['inbox', 'autolabel'] as const

export function useConversations(
  filters: InboxFilters,
  window: { limit: number; cursor?: string } = { limit: 50 },
): UseQueryResult<Paginated<ConversationSummary>> {
  return useQuery({
    // The window is in the key as well as in the request, or Next serves the
    // held first page back and the thread list never moves.
    queryKey: qk.conversations({ ...filters, limit: window.limit, cursor: window.cursor ?? '' }),
    queryFn: () =>
      api.get<Paginated<ConversationSummary>>('/conversations', {
        params: {
          scope: filters.master ? 'all' : null,
          brandId: filters.master ? filters.brandId : null,
          /*
            Each list as one comma-separated parameter. A single value also
            rides the old single parameter, which the server still reads and
            adds to the same list, so a link from before the lists, and a server
            from before them, still narrow the list rather than ignore it.
          */
          campaignIds: listParam(filters.campaignIds),
          campaignId: filters.campaignIds.length === 1 ? filters.campaignIds[0] : null,
          channels: listParam(filters.channels),
          channel: filters.channels.length === 1 ? filters.channels[0] : null,
          senderAccountIds: listParam(filters.senderAccountIds),
          senderAccountId: filters.senderAccountIds.length === 1 ? filters.senderAccountIds[0] : null,
          excludeCampaignIds: listParam(filters.excludeCampaignIds),
          excludeChannels: listParam(filters.excludeChannels),
          excludeSenderAccountIds: listParam(filters.excludeSenderAccountIds),
          mine: filters.mine ? 'true' : null,
          owner: filters.owner ?? null,
          reminder: listParam(filters.reminder),
          classification: filters.classification,
          needsReview: filters.needsReview ? 'true' : null,
          q: filters.q.trim() || null,
          state: filters.state,
          view: filters.view,
          /*
            Never both. The server answers 400 to the pair rather than guessing
            which one was meant, and `labelFiltersFrom` has already resolved it,
            so this is the second half of one rule rather than a second rule.
          */
          labelId: filters.labelId,
          unlabelled: filters.unlabelled && filters.labelId === null ? 'true' : null,
          aiDraft: filters.aiDraft ? 'true' : null,
          limit: window.limit,
          cursor: window.cursor,
        },
      }),
    refetchInterval: 60_000,
    /*
      The list also reads itself again when the window comes back into focus,
      which the app's default turns off. The inbox had a Refresh button beside
      its search, and the reference's bar has none: a reply that landed while
      somebody was in another tab is on screen the moment they come back,
      rather than up to a minute later.
    */
    refetchOnWindowFocus: true,
  })
}

export function useConversation(id: string | null): UseQueryResult<Conversation> {
  return useQuery({
    queryKey: qk.conversation(id ?? 'none'),
    queryFn: () => api.get<Conversation>(`/conversations/${id}`),
    enabled: id !== null,
  })
}

function useAfterInboxChange(): (conversationId?: string) => void {
  const client = useQueryClient()
  return (conversationId?: string) => {
    void client.invalidateQueries({ queryKey: CONVERSATIONS_ROOT })
    if (conversationId) void client.invalidateQueries({ queryKey: qk.conversation(conversationId) })
    void client.invalidateQueries({ queryKey: APPROVALS_ROOT })
    void client.invalidateQueries({ queryKey: CARDS_ROOT })
    /*
      THE RAIL IS A SECOND READING OF THE SAME THREADS and it goes stale on
      every one of these. Archiving a thread takes it out of All and puts it in
      Archived, marking one read takes one off Unread, and a rail still showing
      the old number beside a list that has already moved reads as the count
      being wrong rather than as the count being old. One extra invalidation
      buys one extra request, and that request is the whole column.
    */
    void client.invalidateQueries({ queryKey: INBOX_COUNTS_ROOT })
  }
}

/**
 * Write a reply into the thread's composer. Nothing goes out from here.
 *
 * THE OLD RETURN TYPE NAMED A QUEUE THIS DRAFT COULD NEVER REACH. It read
 * `{ approvalId }`, and the copy built on it told the operator the reply was
 * waiting in the approval queue. It never was: the approvals route reaches
 * generated content through an inner join on the enrollment step, and a reply
 * draft carries none, so the queue excluded these rows by construction and the
 * id pointed at a list that could not contain them. The server hands back its
 * own next step now and the screen says that instead of guessing.
 */
export function useDraftReply() {
  const after = useAfterInboxChange()
  return useMutation({
    /*
      The words, and on email the html, the copies and the history choice
      (`replyPayload` decides which, by channel), so a saved draft reopens
      with all of them.
    */
    mutationFn: (input: { conversationId: string } & ReplyPayload) =>
      api.post<{ draftId: string; conversationId: string; state: 'pending'; nextStep: string }>(
        `/conversations/${input.conversationId}/reply`,
        replyRequestBody(input),
      ),
    onSuccess: (_data, variables) => after(variables.conversationId),
  })
}

/**
 * Hand a drafted reply to the dispatcher.
 *
 * THE DISPATCHER IS THE ONLY WAY OUT and this route is the only way to it from
 * a screen. Everything that keeps this product from burning an account lives
 * behind it: the quota reservation, the pacing gap, the suppression re-check,
 * the quiet hours, and the two brakes. A send that went straight to a mailbox
 * or straight to the device queue would pass every one of those without
 * touching it. A change in this repo did exactly that recently and had to be
 * parked, so this is a named hazard rather than a style preference.
 *
 * The answer is two different facts wearing one word. `sent` means it went
 * inside this call and the thread has an outbound message on it now. `sending`
 * means it is queued on the sending account and THE THREAD HAS NOTHING ON IT,
 * which is why the caller must not draw it as correspondence.
 */
export function useSendReplyDraft() {
  const after = useAfterInboxChange()
  return useMutation({
    mutationFn: (input: { draftId: string; conversationId: string } & Partial<ReplyPayload>) =>
      api.post<ReplySendResult>(`/replies/drafts/${input.draftId}/send`, replyRequestBody(input)),
    onSuccess: (_data, variables) => after(variables.conversationId),
  })
}

export function useSnoozeConversation() {
  const after = useAfterInboxChange()
  return useMutation({
    mutationFn: (input: { conversationId: string; until: string }) =>
      api.post<{ conversationId: string; snoozedUntil: string }>(
        `/conversations/${input.conversationId}/snooze`,
        { until: input.until },
      ),
    onSuccess: (_data, variables) => after(variables.conversationId),
  })
}

export function useDisqualifyConversation() {
  const after = useAfterInboxChange()
  return useMutation({
    mutationFn: (input: { conversationId: string; reason: string; note?: string; suppress: boolean }) =>
      api.post<{
        conversationId: string
        enrollmentsEnded: number
        suppressionsWritten: number
        holdUntil?: string | null
        warning?: string
      }>(`/conversations/${input.conversationId}/disqualify`, {
        reason: input.reason,
        ...(input.note === undefined ? {} : { note: input.note }),
        suppress: input.suppress,
      }),
    onSuccess: (_data, variables) => after(variables.conversationId),
  })
}

/**
 * The standalone "a reply arrived somewhere else" action. Used from the inbox and
 * from an inbox-sweep card in the work queue, which is why it lives here rather
 * than beside either screen.
 */
export function useLogInboundReply() {
  const after = useAfterInboxChange()
  return useMutation({
    mutationFn: (input: LogReplyInput) =>
      api.post<{ conversationId: string }>('/replies/log', input),
    onSuccess: () => after(),
  })
}

// ---------------------------------------------------------------------------
// Lookups for the filter bar and the log-a-reply form
// ---------------------------------------------------------------------------

/** The whole roster, because these are the options on a filter. See the copy in dashboard/ui/data.ts. */
export function useChannelAccounts(): UseQueryResult<Paginated<ChannelAccount>> {
  return useQuery({
    queryKey: [...qk.channelAccounts(), COMPLETE_LIST_LIMIT],
    queryFn: () =>
      api.get<Paginated<ChannelAccount>>('/channel-accounts', { params: { limit: COMPLETE_LIST_LIMIT } }),
    staleTime: 5 * 60_000,
  })
}

/** The whole list, because these are the options on a filter. */
export function useCampaigns(): UseQueryResult<Paginated<CampaignSummary>> {
  return useQuery({
    queryKey: [...qk.campaigns(), COMPLETE_LIST_LIMIT],
    queryFn: () => api.get<Paginated<CampaignSummary>>('/campaigns', { params: { limit: COMPLETE_LIST_LIMIT } }),
    staleTime: 5 * 60_000,
  })
}

/** Prospect typeahead for the log-a-reply form. Two letters before it asks. */
export function useProspectSearch(query: string): UseQueryResult<Paginated<ProspectSummary>> {
  const trimmed = query.trim()
  return useQuery({
    queryKey: qk.prospects({ q: trimmed, limit: 10 }),
    queryFn: () =>
      api.get<Paginated<ProspectSummary>>('/prospects', { params: { q: trimmed, limit: 10 } }),
    enabled: trimmed.length >= 2,
    staleTime: 30_000,
  })
}

// ---------------------------------------------------------------------------
// Triage: read state, reminders and the archive
// ---------------------------------------------------------------------------

/**
 * The rail's numbers, in one request for the whole column.
 *
 * `enabled` is the interesting parameter. `GET /inbox/counts` is scoped to the
 * session's active brand and has no cross-brand form, so in the admin
 * all-brands view every number here would describe one brand while the list
 * beside it describes all of them. A badge that is wrong is worse than a badge
 * that is missing, so the request is not made at all there and the rail draws
 * no counts. The column says so in one line rather than leaving somebody to
 * wonder where the numbers went.
 *
 * `owner` is the person menu's choice: the numbers then count that person's
 * accounts, like the list. The sidebar's badge asks without one.
 */
export function useInboxCounts(enabled: boolean, owner: string | null = null): UseQueryResult<InboxCounts> {
  return useQuery({
    // The person menu's choice is part of the key, under the same root, so
    // everything that refreshes the counts refreshes every person's.
    queryKey: owner === null ? [...INBOX_COUNTS_ROOT] : [...INBOX_COUNTS_ROOT, owner],
    queryFn: () => api.get<InboxCounts>(owner === null ? '/inbox/counts' : `/inbox/counts?owner=${encodeURIComponent(owner)}`),
    enabled,
    refetchInterval: 60_000,
    // With the list, so the rail and the rows it counts move together.
    refetchOnWindowFocus: true,
  })
}

/**
 * Mark a thread read.
 *
 * AN EXPLICIT WRITE, because the server refuses to do it on a read. Opening a
 * thread is a GET, and that GET is reachable by an `inbox:read` API token and
 * by the admin cross-brand walk, so a read-shaped request that quietly wrote
 * the flag would mean auditing every caller before anybody could trust it. The
 * console is the thing that knows a person is looking, so the console is the
 * thing that says so.
 */
export function useMarkRead() {
  const writeReadState = useReadStateWrite()
  return useMutation({
    mutationFn: (input: { conversationId: string }) =>
      api.post<{ conversationId: string; unread: false; readAt: string }>(
        `/conversations/${input.conversationId}/read`,
      ),
    onMutate: (variables) =>
      writeReadState.before(variables.conversationId, { unread: false, readAt: new Date().toISOString() }),
    onSuccess: (data, variables) =>
      writeReadState.from(variables.conversationId, { unread: data.unread, readAt: data.readAt }),
    onSettled: (_data, _error, variables) => writeReadState.after(variables.conversationId),
  })
}

/** Put a thread back on the Unread tab, which is the toolbar's Unread button. */
export function useMarkUnread() {
  const writeReadState = useReadStateWrite()
  return useMutation({
    mutationFn: (input: { conversationId: string }) =>
      api.post<{ conversationId: string; unread: true; readAt: null }>(
        `/conversations/${input.conversationId}/unread`,
      ),
    onMutate: (variables) => writeReadState.before(variables.conversationId, { unread: true, readAt: null }),
    onSuccess: (data, variables) =>
      writeReadState.from(variables.conversationId, { unread: data.unread, readAt: data.readAt }),
    onSettled: (_data, _error, variables) => writeReadState.after(variables.conversationId),
  })
}

/**
 * Read state written into the open thread's cache, in three steps.
 *
 * WHY INVALIDATING WAS NOT ENOUGH. The browser pass loaded a thread cold and
 * sampled the toolbar every 500ms. It read "Read" for all 6.5 seconds while
 * the server already held the thread as read, and a reload corrected it. The
 * thread GET and the automatic mark-read POST start together, the GET answered
 * with the value from before the write, and the cache kept it.
 *
 * So, in order. BEFORE the request: cancel whatever GET is in flight for this
 * thread, so an answer from before the write cannot land after it, then write
 * the value being asked for. FROM the response: write it again, in the server's
 * own words, which matters for `readAt`. AFTER, success or failure: invalidate as
 * before, so the list, the rail and the thread all read the server back. A
 * failed write is corrected by that refetch rather than by a rollback kept here.
 */
function useReadStateWrite() {
  const client = useQueryClient()
  const after = useAfterInboxChange()
  const write = (conversationId: string, next: { unread: boolean; readAt: string | null }) =>
    client.setQueryData<Conversation>(qk.conversation(conversationId), (cached) =>
      withReadState(cached, next),
    )
  return {
    before: async (conversationId: string, next: { unread: boolean; readAt: string | null }) => {
      await client.cancelQueries({ queryKey: qk.conversation(conversationId), exact: true })
      write(conversationId, next)
    },
    from: (conversationId: string, next: { unread: boolean; readAt: string | null }) => {
      write(conversationId, next)
    },
    after: (conversationId: string) => after(conversationId),
  }
}

/**
 * Ask to be brought back to a thread.
 *
 * NOTHING FIRES ON IT. There is no job behind this and no notification
 * anywhere, so it is a filter and only a filter: the thread appears on the
 * Reminders tab from that moment. Copy near this must not promise a ping.
 */
export function useSetReminder() {
  const after = useAfterInboxChange()
  return useMutation({
    mutationFn: (input: { conversationId: string; at: string }) =>
      api.post<{ conversationId: string; remindAt: string; fires: false }>(
        `/conversations/${input.conversationId}/remind`,
        { at: input.at },
      ),
    onSuccess: (_data, variables) => after(variables.conversationId),
  })
}

export function useClearReminder() {
  const after = useAfterInboxChange()
  return useMutation({
    mutationFn: (input: { conversationId: string }) =>
      api.del<{ conversationId: string; remindAt: null }>(
        `/conversations/${input.conversationId}/remind`,
      ),
    onSuccess: (_data, variables) => after(variables.conversationId),
  })
}

/**
 * File a thread away. This is what the Delete button does.
 *
 * It writes `state = 'archived'`, a value the schema has allowed since the
 * first migration and nothing had ever written. Nothing is removed: this
 * platform redacts rather than deletes, and the message ledger is half of what
 * makes the quota counters mean anything. The Move button is the same route,
 * because there is one archive and not two mechanisms.
 */
export function useArchiveConversation() {
  const after = useAfterInboxChange()
  return useMutation({
    mutationFn: (input: { conversationId: string }) =>
      api.post<{ conversationId: string; state: 'archived' }>(
        `/conversations/${input.conversationId}/archive`,
      ),
    onSuccess: (_data, variables) => after(variables.conversationId),
  })
}

export function useRestoreConversation() {
  const after = useAfterInboxChange()
  return useMutation({
    mutationFn: (input: { conversationId: string }) =>
      api.post<{ conversationId: string; state: 'open' }>(
        `/conversations/${input.conversationId}/restore`,
      ),
    onSuccess: (_data, variables) => after(variables.conversationId),
  })
}

// ---------------------------------------------------------------------------
// The workspace's own labels
// ---------------------------------------------------------------------------

/**
 * Every label the brand has.
 *
 * The whole list, because these are the options on a control and because a
 * workspace with more labels than a page of them is not a workspace anybody is
 * filing by hand. The rail gets its labels off the counts endpoint instead,
 * which already carries name and colour beside the number, so this one is for
 * the editor and for the picker rather than for the column.
 */
export function useLabels(): UseQueryResult<Paginated<InboxLabel>> {
  return useQuery({
    queryKey: [...LABELS_ROOT, COMPLETE_LIST_LIMIT],
    queryFn: () => api.get<Paginated<InboxLabel>>('/labels', { params: { limit: COMPLETE_LIST_LIMIT } }),
    staleTime: 60_000,
  })
}

/**
 * Invalidation for a change to the labels themselves.
 *
 * Three caches, and leaving any one of them out shows somebody a stale answer.
 * The label list is the obvious one. The counts carry the rail's copy of every
 * label, so a rename that skipped them would leave the old word in the column
 * until something else happened. And the conversation lists carry the pills on
 * each row, which are the same name again.
 */
function useAfterLabelChange(): () => void {
  const client = useQueryClient()
  return () => {
    void client.invalidateQueries({ queryKey: LABELS_ROOT })
    void client.invalidateQueries({ queryKey: INBOX_COUNTS_ROOT })
    void client.invalidateQueries({ queryKey: CONVERSATIONS_ROOT })
  }
}

export function useCreateLabel() {
  const after = useAfterLabelChange()
  return useMutation({
    mutationFn: (input: { name: string; colour: string; applyWhen?: string | null }) =>
      api.post<InboxLabel>('/labels', {
        name: input.name,
        colour: input.colour,
        // Left out when blank, so a server from before Auto-label, which
        // refuses a key it does not know, still takes the label.
        ...(input.applyWhen === undefined || input.applyWhen === null ? {} : { applyWhen: input.applyWhen }),
      }),
    onSuccess: after,
  })
}

export function useUpdateLabel() {
  const after = useAfterLabelChange()
  return useMutation({
    mutationFn: (input: { id: string; name?: string; colour?: string; applyWhen?: string | null }) =>
      api.patch<InboxLabel>(`/labels/${input.id}`, {
        ...(input.name === undefined ? {} : { name: input.name }),
        ...(input.colour === undefined ? {} : { colour: input.colour }),
        ...(input.applyWhen === undefined ? {} : { applyWhen: input.applyWhen }),
      }),
    onSuccess: after,
  })
}

/**
 * Delete a label.
 *
 * It comes off every thread that carried it, by cascade, and that loses the
 * record that those threads were once filed under it. That is accepted: a
 * label is an operator's own filing rather than evidence about a person, and
 * the audit row naming who removed it survives. The dialog says so before it
 * asks.
 */
export function useDeleteLabel() {
  const after = useAfterLabelChange()
  return useMutation({
    mutationFn: (input: { id: string }) =>
      api.del<{ id: string; deleted: true }>(`/labels/${input.id}`),
    onSuccess: after,
  })
}

/**
 * Put one of the workspace's labels on a thread.
 *
 * `attached: false` is not a failure. The pair is the join table's primary key,
 * so a double press inserts twice and the database refuses the second, and the
 * thread carries the label either way. Nothing here treats it as an error.
 */
export function useAttachLabel() {
  const after = useAfterInboxChange()
  return useMutation({
    mutationFn: (input: { conversationId: string; labelId: string }) =>
      api.post<{ conversationId: string; labelId: string; attached: boolean }>(
        `/conversations/${input.conversationId}/labels`,
        { labelId: input.labelId },
      ),
    onSuccess: (_data, variables) => after(variables.conversationId),
  })
}

export function useDetachLabel() {
  const after = useAfterInboxChange()
  return useMutation({
    mutationFn: (input: { conversationId: string; labelId: string }) =>
      api.del<{ conversationId: string; labelId: string; detached: boolean }>(
        `/conversations/${input.conversationId}/labels/${input.labelId}`,
      ),
    onSuccess: (_data, variables) => after(variables.conversationId),
  })
}

// ---------------------------------------------------------------------------
// Auto-label (0153)
// ---------------------------------------------------------------------------

/**
 * The brand's Auto-label switch, and whether this server can run it.
 *
 * Off in the all-brands view, because the switch belongs to one brand and the
 * view is several.
 */
export function useAutoLabel(enabled = true): UseQueryResult<AutoLabelState> {
  return useQuery({
    queryKey: AUTOLABEL_ROOT,
    queryFn: () => api.get<AutoLabelState>('/inbox/autolabel'),
    enabled,
    staleTime: 60_000,
  })
}

/** Turn it on or off. The answer is the stored state, which replaces the cached one. */
export function useSetAutoLabel() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { enabled: boolean }) => api.put<AutoLabelState>('/inbox/autolabel', input),
    onSuccess: (state) => client.setQueryData(AUTOLABEL_ROOT, state),
  })
}

/**
 * Label open threads now: one job per open thread with a reply, newest first,
 * as many as the brand's setting allows. The labels arrive as each thread is
 * read, minutes later, so nothing here waits for them.
 */
export function useLabelOpenThreads() {
  return useMutation({
    mutationFn: () => api.post<AutoLabelOpenThreadsResult>('/inbox/autolabel/label-open-threads'),
  })
}
